import type { IFileResourceData } from 'services/fileResources/fileResources.api.types';

// Every file resource type. A type missing here can't be resolved by
// fileBinary.
export const FILE_RESOURCE_NAMES = [
  'contact_images',
  'application_images',
  'inventory_images',
  'gallery_images',
  'campaign_images',
  'mission_images',
  'media_assets',
  'company_files',
  'contact_document_files',
  'application_document_files',
  'company_document_files',
  'document_templates',
  'out_of_office_files',
  'project_expenses',
  'contact_invoices',
  'payroll_files',
  'contact_payslip_files',
  'mission_files',
  'message_files',
  'signature_files',
  'signature_report_files',
  // generated by the backend, never uploaded
  'export_files',
  // gallery images readable without a login
  'public_gallery_images',
] as const;

export type TFileResourceName = (typeof FILE_RESOURCE_NAMES)[number];

export interface IFileResourceRef {
  entity: TFileResourceName;
  id: number;
  // api-relative path of the file resource, e.g. 'api/contact_images/1'
  resourcePath: string;
}

const isFileResourceName = (value: string): value is TFileResourceName =>
  (FILE_RESOURCE_NAMES as readonly string[]).includes(value);

/**
 * Accepts a file resource IRI in any of the forms the app carries around
 * ('/api/contact_images/1', 'api/contact_images/1', the legacy
 * '/api/contact_images/1/file', optionally with a query string) and returns
 * the resource it points to.
 */
export const parseFileIri = (iri?: string | null): IFileResourceRef | null => {
  if (!iri) {
    return null;
  }

  const [path] = iri.split('?');
  const match = /^\/?api\/([a-z_]+)\/(\d+)(?:\/file)?\/?$/.exec(path);

  if (!match || !isFileResourceName(match[1])) {
    return null;
  }

  const entity = match[1];
  const id = parseInt(match[2], 10);

  return { entity, id, resourcePath: `api/${entity}/${id}` };
};

/**
 * Whether a binary has been uploaded to a file resource yet. One can exist
 * without, since upload is two steps (POST the resource, then …/file).
 * The only place allowed to read `file`.
 */
export const hasFileBinary = (
  resource?: Pick<IFileResourceData, 'file'> | null
): boolean => !!resource?.file;
